import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import { DINING_METHOD, extractInventoryIds, ROUTES, customToast } from '@common'
import { Loader, DiningMethodEditModal } from '@components'
import { useCartRedux, useOrderRedux, useUserRedux, useAuth, useMembershipRedux, useCartTotals } from '@hooks'
import { convertToSingaporeDateTime } from '@utils'
import { ErrorTrackingAPI } from '@services'
import InventoryWarningModal from '../InventoryWarningModal'
// import PaymentMethodEditModal from '../PaymentMethodEditModal'

const CartFooter = () => {
    const navigate = useNavigate()
    const {
        items,
        creditToUseCents,
        isComplimentaryOrder,
        complimentaryOrderReason,
        checkInventoryStatus,
        inventoryChanges,
        clientRequestId,
        initClientRequestId,
    } = useCartRedux()
    const { isLoading, submitOrder, submitMemberOrder, fetchAvailablePromos, updatePromoCheckTimestamp } = useOrderRedux()
    const { diningMethod, tableId, pickupDate, pickupTime } = useUserRedux()
    const { memberData, isTokenValid } = useAuth()
    const { checkPendingRatings } = useMembershipRedux()
    const { totals: total, eligiblePromos, promoDiscount, requestedPromos, discountPct } = useCartTotals()

    const isEmpty = useMemo(() => items.length === 0, [items])
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false)
    // const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false)
    // const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD_OPTIONS[0])
    // const paymentMethod = PAYMENT_METHOD_OPTIONS[0]
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isProcessing = useMemo(() => isSubmitting || isLoading || isEmpty, [isSubmitting, isLoading, isEmpty])

    useEffect(() => {
        initClientRequestId()
        // eslint-disable-next-line
    }, [])

    const handleModalConfirm = async () => {
        if (isProcessing) return
        setIsInventoryModalOpen(false)
        setIsSubmitting(true)
        try {
            await processOrder()
        } finally {
            setIsSubmitting(false)
        }
    }

    const navigateAfterOrderCreation = (orderId, diningMethodValue, finalAmount) => {
        // Navigate based on dining method
        if (diningMethodValue === DINING_METHOD.AdvanceTakeaway) {
            // If final amount is $0.00 (free after credit discount), skip payment and go to order complete
            if (finalAmount === 0) {
                navigate(ROUTES.Order.complete.replace(':orderId', orderId), { replace: true })
            } else {
                // Navigate to payment intent creation page for Advance Takeaway
                navigate(ROUTES.AdvanceTakeawayPayment.root.replace(':orderId', orderId), { replace: true })
            }
        } else {
            // Navigate to order complete page for other dining methods
            navigate(ROUTES.Order.complete.replace(':orderId', orderId), { replace: true })
        }
    }

    const onCheckout = async () => {
        if (isProcessing) return

        // Validate complimentary order reason if enabled
        if (isComplimentaryOrder && !complimentaryOrderReason.trim()) {
            customToast.error('Please enter the reason for complimentary order')
            return
        }

        // Validate final amount for Advance Takeaway: must be >= $0.50 or exactly $0.00
        // Calculate total directly to ensure we get the latest values
        if (diningMethod.value === DINING_METHOD.AdvanceTakeaway) {
            const finalAmount = total.finalAmount
            const STRIPE_MINIMUM = 0.5

            // Check if user is still authenticated (session might have expired)
            // This is important because user might have been logged out in another tab
            // We check both Redux state and localStorage token to handle cross-tab scenarios
            const isTokenStillValid = isTokenValid()
            if (!isTokenStillValid) {
                customToast.error('Your access has expired. Please log in again.')
                navigate(ROUTES.Membership.auth, { replace: true })
                return
            }

            // Amount must be 0 (fully redeemed) or >= 0.5 (minimum payment)
            if (finalAmount !== 0 && finalAmount < STRIPE_MINIMUM) {
                customToast.error(
                    `For Advance Takeaway orders, you can either fully redeem to $0.00 or pay at least $${STRIPE_MINIMUM.toFixed(2)}`,
                    { duration: 5000 }
                )
                return
            }
        }

        setIsSubmitting(true)
        try {
            const ids = extractInventoryIds(items)
            const res = await checkInventoryStatus(ids)
            if (res.meta.requestStatus === 'fulfilled') {
                const { isAllActive } = res.payload
                if (isAllActive) {
                    await processOrder()
                } else {
                    setIsInventoryModalOpen(true)
                }
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleOrderFailure = async errorPayload => {
        // Extract error message from payload (could be string or Error object)
        const errorMessage = typeof errorPayload === 'string' ? errorPayload : errorPayload?.message || 'Unknown error'

        // Show error message to user
        customToast.error(errorMessage, { duration: 5000 })

        // Trigger promo recalculation by:
        // 1. Refreshing available promos from server (in case promo rules changed)
        // 2. Updating timestamp to force eligibility recalculation
        await fetchAvailablePromos()
        updatePromoCheckTimestamp()
    }

    const processOrder = async () => {
        // Get memberId from memberData if authenticated
        const memberId = parseInt(memberData?.id) || null
        const isLoggedIn = !!memberId

        // Combine pickupDate and pickupTime if both exist (Singapore timezone UTC+8)
        let pickupScheduleDateTime = null
        if (pickupDate && pickupTime) {
            const dateStr = pickupDate.value || pickupDate
            const timeStr = pickupTime.value || pickupTime
            pickupScheduleDateTime = convertToSingaporeDateTime(dateStr, timeStr, 'normal')
        }

        // Prepare common dish data
        const dishesData = items.map(({ stallName, quantity, ...rest }) => ({
            ...rest,
            tags: undefined,
            variants: rest.variants.map(v => ({
                ...v,
                inventory: undefined,
                selections: undefined,
            })),
            selections: rest.selections.map(s => ({
                ...s,
                options: s.options.map(o => ({
                    ...o,
                    inventory: undefined,
                })),
            })),
        }))

        let orderInput

        if (isLoggedIn) {
            // Prepare order input for logged-in members
            orderInput = {
                clientRequestId,
                tableId: tableId?.value,
                isDineIn: diningMethod.value === DINING_METHOD.DineIn,
                pickupScheduleDateTime,
                isComplimentaryOrder: isComplimentaryOrder || false,
                note: isComplimentaryOrder ? `Complimentary Order - Reason: ${complimentaryOrderReason}` : null,
                useCredit: creditToUseCents > 0 ? creditToUseCents : null,
                dishes: dishesData,
            }
            if (requestedPromos.length > 0) {
                orderInput.requestedPromos = requestedPromos
            }

            // Check pending ratings before creating order for logged-in members
            try {
                const pendingRatingsRes = await checkPendingRatings()

                if (pendingRatingsRes.meta.requestStatus === 'fulfilled' && pendingRatingsRes.payload.hasPendingRatings) {
                    // Has pending ratings, navigate to force rating page with order data
                    navigate(ROUTES.ForceRating.root, {
                        state: {
                            orderInput,
                            diningMethod: diningMethod.value,
                            total: total.finalAmount,
                        },
                    })
                    return
                }
            } catch (error) {
                ErrorTrackingAPI.sendError(error, 'CartFooter', 'checkPendingRatings')
                // Continue with order creation if check fails
            }

            // No pending ratings, proceed with order creation
            const res = await submitMemberOrder(orderInput)

            if (res.meta.requestStatus === 'fulfilled') {
                navigateAfterOrderCreation(res.payload.orderId, diningMethod.value, total.finalAmount)
            } else {
                await handleOrderFailure(res.payload)
            }
        } else {
            // Use createOrder mutation for non-logged-in users
            orderInput = {
                clientRequestId,
                tableId: tableId?.value,
                isDineIn: diningMethod.value === DINING_METHOD.DineIn,
                dishes: dishesData,
            }
            if (requestedPromos.length > 0) {
                orderInput.requestedPromos = requestedPromos
            }
            const res = await submitOrder(orderInput)

            if (res.meta.requestStatus === 'fulfilled') {
                navigateAfterOrderCreation(res.payload.orderId, diningMethod.value, total.finalAmount)
            } else {
                await handleOrderFailure(res.payload)
            }
        }
    }

    // Check if any summary items are displayed above Total
    const hasSummaryItems = useMemo(() => {
        return (
            total.amount !== total.finalAmount ||
            isComplimentaryOrder ||
            (!isComplimentaryOrder && total.roleDiscountAmount > 0) ||
            (!isComplimentaryOrder && total.discountAmount > 0) ||
            promoDiscount > 0 ||
            eligiblePromos.length > 0
        )
    }, [total, isComplimentaryOrder, promoDiscount, eligiblePromos.length])

    return (
        <div className="cart-footer">
            {isLoading && <Loader />}
            <div className="cart-footer-summary">
                <div className="items-container cart-footer-summary-list">
                    {(total.amount !== total.finalAmount || promoDiscount > 0) && (
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="cart-footer-label">Subtotal</span>
                            <span className="cart-footer-value">${total.amount.toFixed(2)}</span>
                        </div>
                    )}
                    {isComplimentaryOrder && (
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="cart-footer-label">Complimentary Order</span>
                            <span className="cart-footer-value text-danger">-${total.amount.toFixed(2)}</span>
                        </div>
                    )}
                    {!isComplimentaryOrder && total.roleDiscountAmount > 0 && (
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="cart-footer-label">
                                Staff Benefit Discount ({discountPct === 100 ? 'Free' : `${discountPct}%`})
                            </span>
                            <span className="cart-footer-value text-danger">-${total.roleDiscountAmount.toFixed(2)}</span>
                        </div>
                    )}
                    {!isComplimentaryOrder && total.discountAmount > 0 && (
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="cart-footer-label">Points Discount</span>
                            <span className="cart-footer-value text-danger">-${total.discountAmount.toFixed(2)}</span>
                        </div>
                    )}
                    {promoDiscount > 0 && (
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="cart-footer-label">Promo Discount</span>
                            <span className="cart-footer-value text-danger">-${promoDiscount.toFixed(2)}</span>
                        </div>
                    )}
                    {eligiblePromos.length > 0 && (
                        <div className="mt-2">
                            <div className="cart-footer-label fw-bold mb-1">Eligible promotions</div>
                            {eligiblePromos.map(promo => (
                                <div key={promo.promoType} className="d-flex justify-content-between align-items-center">
                                    <span className="cart-footer-label">{promo.displayName}</span>
                                    <i className="bx bx-sm bxs-check-circle text-primary"></i>
                                </div>
                            ))}
                        </div>
                    )}
                    <div
                        className={`d-flex justify-content-between align-items-center cart-footer-total ${hasSummaryItems ? 'has-divider' : ''}`}
                    >
                        <span className="cart-footer-label fw-bold">Total</span>
                        <span className="cart-footer-value fw-bold">
                            ${total.finalAmount === 0 ? 'Free' : total.finalAmount.toFixed(2)}
                        </span>
                    </div>
                </div>
            </div>
            <div className="cart-footer-summary">
                <div className="items-container">
                    <div className="d-flex align-items-center justify-content-between" onClick={() => setIsEditModalOpen(true)}>
                        {/* <h6 className="m-0">{diningMethod.value === DINING_METHOD.DineIn ? 'Dine in' : diningMethod.value}</h6> */}
                        <h6 className="m-0">{diningMethod.label}</h6>
                        <i className="bx bx-edit bx-xs text-secondary ms-2 edit-icon"></i>
                    </div>
                    {diningMethod.value === DINING_METHOD.AdvanceTakeaway && (
                        <p>
                            {moment(pickupDate).format('DD MMM, YYYY')} @ {pickupTime}
                        </p>
                    )}
                </div>
            </div>
            {/* <div className="cart-footer-summary">
                <div className="items-container">
                    <div
                        className="d-flex align-items-center justify-content-between"
                        onClick={() => setIsPaymentMethodModalOpen(true)}
                    >
                        <h6 className="m-0">Payment Method</h6>
                        <i className="bx bx-edit bx-xs text-secondary ms-2 edit-icon"></i>
                    </div>
                    <p className={`${paymentMethod.value === PAYMENT_METHOD.PayNow ? 'text-info' : 'text-dark'}`}>
                        {paymentMethod.label}
                    </p>
                </div>
            </div> */}
            <button
                type="button"
                className="btn btn-primary btn-lg w-100 cart-footer-button"
                disabled={isProcessing}
                onClick={onCheckout}
            >
                {isLoading || isSubmitting ? 'Processing...' : 'Checkout'}
            </button>

            <DiningMethodEditModal
                isOpen={isEditModalOpen}
                toggle={() => setIsEditModalOpen(!isEditModalOpen)}
                currentDiningMethod={diningMethod}
                currentTableId={tableId}
                hasItems={items.length > 0}
            />

            {/* <PaymentMethodEditModal
                isOpen={isPaymentMethodModalOpen}
                toggle={() => setIsPaymentMethodModalOpen(!isPaymentMethodModalOpen)}
                currentPaymentMethod={paymentMethod}
                onPaymentMethodChange={setPaymentMethod}
            /> */}

            <InventoryWarningModal
                isOpen={isInventoryModalOpen}
                isProcessing={isProcessing}
                onCancel={() => setIsInventoryModalOpen(false)}
                onConfirm={handleModalConfirm}
                inventoryChanges={inventoryChanges}
            />
        </div>
    )
}

export default CartFooter
